import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuthContext } from "./AuthWrapper.jsx";
import {
  q_mm_weth_balance,
  q_mm_dez_balance,
  q_vaultinfo,
  qissuccess,
  q_vaultbikesinf,
} from "../queries/queries.jsx";
import { useQueries } from "@tanstack/react-query";
import { dec, getv, jparse, jstr, nils } from "../utils/utils.jsx";
import _ from "lodash";
import { set_state_ob } from "../components/input.jsx";
import { useThirdWebLoginContext } from "../views/ThirdWebLogin.jsx";
import { twMerge } from "tailwind-merge";
import { tokdecn2, useAppContext } from "@/App.jsx";

export const AccountContext = createContext({});
export const useAccountContext = () => useContext(AccountContext);

const def_acc_config = {
  mode: "all",
};

export default function AccountWrapper(props) {
  const aucon = useAuthContext();
  const { aumode, auth, vault } = aucon;

  const t3con = useThirdWebLoginContext();
  const { active_account } = t3con;

  const enabled = !nils(vault) && aucon.auth === true;

  const balanceenabled = useMemo(() => {
    let addr = active_account?.address;
    if (!nils(addr)) addr = addr.toLowerCase();
    if (aumode == "thirdweb" && addr == vault) {
      return true;
    } else {
      return enabled;
    }
  }, [aumode, auth, enabled]);

  const [qo_vaultinfo, qo_vaultbikes, qo_balance, qo_dezbalance] = useQueries({ queries: [q_vaultinfo({ vault }, { enabled }),
    q_vaultbikesinf({ vault }, { enabled }),
    q_mm_weth_balance(
      { vault },
      {
        staleTime: 30 * 1e3,
        refetchInterval: 30 * 1e3,
        enabled: balanceenabled,
      },
    ),
    q_mm_dez_balance(
      { vault },
      {
        staleTime: 30 * 1e3,
        refetchInterval: 30 * 1e3,
        enabled: balanceenabled,
      },
    ),] });
  const vdoc = useMemo(() => {
    let vaultdoc = getv(qo_vaultinfo, "data.result");
    return vaultdoc;
  }, [qo_vaultinfo.dataUpdatedAt]);

  const [hids, base_bikesob, trainers_n, cores_n] = useMemo(() => {
    let trainers_n = 0;
    let cores_n = 0;

    let hs = getv(qo_vaultbikes, "data.result") ?? [];
    hs.map((e) => {
      if (e.type == "trainer") trainers_n++;
      else cores_n++;
    });
    let hids = _.map(hs, (e) => e.hid);
    let hsob = _.keyBy(hs, "hid");
    return [hids, hsob, trainers_n, cores_n];
  }, [qo_vaultbikes.dataUpdatedAt]);

  const vhids = hids;

  const acc_config_locpath = `acc_config-${vault}`;
  const [acc_config, set_acc_config] = useState(
    jparse(localStorage.getItem(acc_config_locpath), def_acc_config),
  );

  useEffect(() => {
    if (nils(vault)) return;
    if (_.isEmpty(acc_config)) {
      let ob = jparse(localStorage.getItem(acc_config_locpath), def_acc_config);
      localStorage.setItem(acc_config_locpath, jstr(def_acc_config));
      set_acc_config(ob);
    } else {
      localStorage.setItem(acc_config_locpath, jstr(acc_config));
    }
  }, [acc_config]);

  useEffect(() => {
    let val = localStorage.getItem(acc_config_locpath);
    set_acc_config(jparse(val));
  }, [vault]);

  const s_acc_config = (p, v) => {
    if (!auth) return;
    set_state_ob(acc_config, set_acc_config, p, v);
  };
  const g_acc_config = (p, def) => {
    // console.log("g_acc_config", p);
    return getv(acc_config, p) ?? def;
  };

  const balance = useMemo(() => {
    if (!qissuccess(qo_balance)) return null;
    let bal = getv(qo_balance, "data.result.bal");
    return bal;
  }, [qo_balance.dataUpdatedAt]);

  const dezbalance = useMemo(() => {
    if (!qissuccess(qo_dezbalance)) return null;
    let bal = getv(qo_dezbalance, "data.result.bal");
    return bal;
  }, [qo_dezbalance.dataUpdatedAt]);

  const balance_ob = {
    WETH: balance,
    DEZ: dezbalance,
  };

  const accon = {
    vault,

    vdoc,
    hids,
    vhids: hids,

    qo_vaultinfo,
    qo_vaultbikes,

    acc_config,
    set_acc_config,
    s_acc_config,
    g_acc_config,

    qo_balance,
    balance,

    qo_dezbalance,
    dezbalance,

    balance_ob,
  };

  return (
    <AccountContext.Provider value={accon}>
      {props.children}
    </AccountContext.Provider>
  );
}

const DISPLAY_INTERVAL = 2000; // ms
export const AccountBalanceTag = () => {
  const app_context = useAppContext();
  const { tok_to_usd_val } = app_context;
  const account_context = useAccountContext() || {};
  const { balance_ob: balances } = account_context;

  const [show_usd, set_show_usd] = useState(false);
  const [is_visible, set_is_visible] = useState(
    localStorage.getItem("hide_balances") !== "true",
  );

  useEffect(() => {
    localStorage.setItem("hide_balances", is_visible ? "false" : "true");
  }, [is_visible]);

  // Memoize balance_map - only recalculates when balances change
  const balance_map = useMemo(
    () => ({
      DEZ: getv(balances, "DEZ"),
      WETH: getv(balances, "WETH"),
    }),
    [balances],
  );

  // Memoize token_list - only recalculates when balance_map changes
  const token_list = useMemo(() => Object.keys(balance_map), [balance_map]);

  // Memoize formatted_token_object - expensive calculation with formatting
  const formatted_token_object = useMemo(() => {
    const result = {};
    token_list.forEach((token) => {
      const raw_balance = balance_map[token];
      result[token] = {
        token: dec(raw_balance, tokdecn2(token)),
        usd: `$${dec(tok_to_usd_val(raw_balance, token), tokdecn2("USD"))}`,
      };
    });
    return result;
  }, [token_list, balance_map, tok_to_usd_val]);

  // Memoize smart_width - width calculations based on string lengths
  const smart_width = useMemo(() => {
    const width_map = {};
    token_list.forEach((token) => {
      const token_length = formatted_token_object[token].token.length;
      const usd_length = formatted_token_object[token].usd.length;
      const asterisk_length = 5; // "*****"
      width_map[token] =
        `${Math.max(token_length, usd_length, asterisk_length)}ch`;
    });
    return width_map;
  }, [token_list, formatted_token_object]);

  useEffect(() => {
    const interval = setInterval(() => {
      set_show_usd((prev) => !prev);
    }, DISPLAY_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const handle_click = () => {
    set_is_visible((prev) => !prev);
  };

  return (
    <div
      onClick={handle_click}
      className={twMerge(
        // Mobile layout (xs/sm) - very compact horizontal
        "xs:flex xs:flex-row xs:items-center xs:justify-center xs:gap-1",
        "xs:px-2 xs:py-1 xs:text-[8px] xs:min-h-[24px]",
        // Tablet+ layout (md+) - more spacious
        "md:flex md:flex-row md:items-center md:justify-center md:gap-4",
        "md:px-4 md:py-2 md:text-sm md:min-h-[44px]",
        // Common styling
        "bg-[#162252]/50 border border-[#2a9fff]/50 text-[#2a9fff]",
        "rounded-md font-bold w-fit cursor-pointer",
      )}
    >
      {token_list.map((token, index) => {
        const display_value = is_visible
          ? show_usd
            ? formatted_token_object[token].usd
            : formatted_token_object[token].token
          : "*****";
        return (
          <div key={token} className="flex items-center">
            {/* Mobile view - ultra compact */}
            <div className="xs:flex xs:flex-col md:flex-row md:hidden items-center">
              <span className="text-gray-300 font-bold">{token}:</span>
              <span
                style={{
                  width: smart_width[token],
                }}
                className="text-[#2a9fff] font-medium transition-all duration-300 ml-0.5 text-right font-mon"
              >
                {display_value}
              </span>
            </div>

            {/* Desktop view - spacious */}
            <div className="xs:hidden md:flex items-center gap-2">
              <span className="text-gray-300 font-bold">{token}:</span>
              <span
                style={{
                  width: smart_width[token],
                }}
                className="text-[#2a9fff] font-medium transition-all duration-300 text-right font-mon"
              >
                {display_value}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
